import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// ─── Global ────────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { overflow-x: hidden; }
`;

// ─── Keyframes ─────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.08); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// ─── Layout ────────────────────────────────────────────────────────────────
const Wrapper = styled.div`
  position: relative;
  background: transparent;
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    #0d0015 0%,
    #1a0035 30%,
    #2d0060 60%,
    #1a0040 100%
  );
`;

// Ambient orbs
const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  animation: ${pulse} ${({ dur }) => dur || "4s"} ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || "0s"};
  ${({ top, left, right, bottom, size, color }) => `
    top: ${top};
    left: ${left};
    right: ${right};
    bottom: ${bottom};
    width: ${size};
    height: ${size};
    background: ${color};
  `}
`;

const Track = styled.div`
  display: flex;
  flex-direction: row;
  width: fit-content;
  height: 100%;
  will-change: transform;
`;

// ─── Panel ─────────────────────────────────────────────────────────────────
const Panel = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  position: relative;
  flex-shrink: 0;
`;

// ─── Intro Panel ───────────────────────────────────────────────────────────
const IntroInner = styled.div`
  text-align: center;
  max-width: 680px;
`;

const Eyebrow = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #c084fc;
  margin-bottom: 1.5rem;
  opacity: 0;
`;

const MainTitle = styled.h2`
  font-family: "Playfair Display", serif;
  font-size: clamp(2.8rem, 6vw, 5.5rem);
  font-weight: 700;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 1.5rem;
  opacity: 0;

  em {
    font-style: italic;
    background: linear-gradient(90deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 3s linear infinite;
  }
`;

const SubText = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 1.125rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.7;
  opacity: 0;
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: 0;

  span {
    font-family: "DM Sans", sans-serif;
    font-size: 0.7rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.35);
  }
`;

const ChevronWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Chevron = styled.div`
  width: 18px;
  height: 18px;
  border-right: 1.5px solid rgba(255, 255, 255, 0.3);
  border-bottom: 1.5px solid rgba(255, 255, 255, 0.3);
  transform: rotate(45deg);
  animation: ${pulse} 1.5s ease-in-out infinite;
  animation-delay: ${({ d }) => d};
`;

// ─── Step Panel ────────────────────────────────────────────────────────────
const StepGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
  max-width: 1100px;
  width: 100%;

  &.reverse {
    direction: rtl;
    > * {
      direction: ltr;
    }
  }
`;

const StepVisual = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 420px;
`;

const VisualRing = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 1px solid ${({ c }) => c || "rgba(168,85,247,0.3)"};
  ${({ s }) => `width: ${s}; height: ${s};`}
  animation: ${pulse} ${({ dur }) => dur} ease-in-out infinite;
  animation-delay: ${({ del }) => del};
`;

const CardMock = styled.div`
  width: 200px;
  height: 280px;
  border-radius: 20px;
  background: linear-gradient(160deg, #2a0050, #4a0080);
  border: 1px solid rgba(168, 85, 247, 0.4);
  overflow: hidden;
  position: relative;
  box-shadow:
    0 40px 80px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  animation: ${floatY} 4s ease-in-out infinite;
  flex-shrink: 0;
`;

const CardGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 40%,
    rgba(0, 0, 0, 0.85) 100%
  );
`;

const CardFace = styled.div`
  width: 100%;
  height: 60%;
  background: ${({ bg }) => bg};
`;

const CardInfo = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;

  h4 {
    font-family: "DM Sans", sans-serif;
    font-size: 1rem;
    font-weight: 500;
    color: #fff;
    margin-bottom: 0.2rem;
  }

  p {
    font-family: "DM Sans", sans-serif;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const HeartBurst = styled.div`
  position: absolute;
  top: 50%;
  right: -24px;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #e879f9, #a855f7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 0 30px rgba(232, 121, 249, 0.5);
`;

// Neural / spark dots
const SparkDot = styled.div`
  position: absolute;
  width: ${({ s }) => s || "8px"};
  height: ${({ s }) => s || "8px"};
  border-radius: 50%;
  background: ${({ c }) => c || "#a855f7"};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  box-shadow: 0 0 12px ${({ c }) => c || "#a855f7"};
  animation: ${pulse} ${({ dur }) => dur || "2s"} ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
`;

const ConnectLine = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

// Phone mockup for step 3
const PhoneMock = styled.div`
  width: 180px;
  height: 340px;
  border-radius: 36px;
  background: linear-gradient(160deg, #1a0035, #2d0060);
  border: 1.5px solid rgba(168, 85, 247, 0.5);
  position: relative;
  overflow: hidden;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.5);
  animation: ${floatY} 5s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 6px;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 3px;
  }
`;

const ChatBubble = styled.div`
  position: absolute;
  background: ${({ mine }) =>
    mine
      ? "linear-gradient(135deg, #7c3aed, #a855f7)"
      : "rgba(255,255,255,0.08)"};
  border-radius: ${({ mine }) =>
    mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px"};
  padding: 0.5rem 0.75rem;
  left: ${({ mine }) => (mine ? "auto" : "1rem")};
  right: ${({ mine }) => (mine ? "1rem" : "auto")};
  max-width: 120px;
  top: ${({ top }) => top};

  p {
    font-family: "DM Sans", sans-serif;
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }
`;

// Step text
const StepContent = styled.div`
  opacity: 0;
  transform: translateX(${({ dir }) => (dir === "left" ? "-60px" : "60px")});
`;

const StepNumber = styled.div`
  font-family: "Playfair Display", serif;
  font-size: 7rem;
  font-weight: 700;
  line-height: 1;
  color: rgba(168, 85, 247, 0.12);
  margin-bottom: -1.5rem;
`;

const StepTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(168, 85, 247, 0.12);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 100px;
  padding: 0.3rem 0.9rem;
  margin-bottom: 1rem;

  span {
    font-family: "DM Sans", sans-serif;
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #c084fc;
  }

  div {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #e879f9;
    box-shadow: 0 0 8px #e879f9;
  }
`;

const StepTitle = styled.h3`
  font-family: "Playfair Display", serif;
  font-size: clamp(2rem, 3.5vw, 3rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 1.25rem;

  em {
    font-style: italic;
    color: #c084fc;
  }
`;

const StepDesc = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.8;
  max-width: 420px;
  margin-bottom: 2rem;
`;

const StatRow = styled.div`
  display: flex;
  gap: 2rem;
`;

const Stat = styled.div`
  div:first-child {
    font-family: "Playfair Display", serif;
    font-size: 2rem;
    font-weight: 700;
    color: #e879f9;
    margin-bottom: 0.2rem;
  }
  div:last-child {
    font-family: "DM Sans", sans-serif;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.35);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;

// ─── Outro Panel ───────────────────────────────────────────────────────────
const OutroInner = styled.div`
  text-align: center;
  max-width: 700px;
  opacity: 0;
`;

const CtaButton = styled.button`
  margin-top: 2.5rem;
  padding: 1rem 3rem;
  background: linear-gradient(135deg, #7c3aed, #a855f7, #e879f9);
  border: none;
  border-radius: 100px;
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  letter-spacing: 0.05em;
  box-shadow: 0 20px 60px rgba(168, 85, 247, 0.4);
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 30px 80px rgba(168, 85, 247, 0.6);
  }
`;

// ─── Step data ──────────────────────────────────────────────────────────────
const STEPS = [
  {
    number: "01",
    tag: "First Look",
    title: (
      <>
        You <em>see</em> them.
      </>
    ),
    desc: "It starts in milliseconds. Your eyes catch someone and your brain fires before you even think. We surface real people, real photos — no filters, no illusions.",
    stats: [
      { val: "0.1s", label: "To feel attracted" },
      { val: "94%", label: "Visual-first decisions" },
    ],
    dir: "right",
  },
  {
    number: "02",
    tag: "The Spark",
    title: (
      <>
        Your brain <em>lights up.</em>
      </>
    ),
    desc: "Attraction isn't a thought — it's a rush of dopamine, a quickened pulse. When you swipe right, you're following your oldest instinct. We just make it easier to act on.",
    stats: [
      { val: "3×", label: "More dopamine" },
      { val: "82%", label: "Trust their gut" },
    ],
    dir: "left",
    reverse: true,
  },
  {
    number: "03",
    tag: "The Connection",
    title: (
      <>
        Now you <em>connect.</em>
      </>
    ),
    desc: "Chemistry confirmed, conversation begins. No awkward icebreakers, no guessing — just two people who already know they're interested. The rest is just details.",
    stats: [
      { val: "67%", label: "Meet in week one" },
      { val: "4.8★", label: "Match quality" },
    ],
    dir: "right",
  },
];

// ─── Visual components per step ────────────────────────────────────────────
function Step1Visual() {
  return (
    <StepVisual>
      <VisualRing s="340px" c="rgba(168,85,247,0.08)" dur="3s" del="0s" />
      <VisualRing s="260px" c="rgba(168,85,247,0.14)" dur="3.5s" del="0.5s" />
      <VisualRing s="180px" c="rgba(232,121,249,0.2)" dur="2.5s" del="0.8s" />
      <CardMock>
        <CardFace bg="linear-gradient(160deg, #4a1060, #7c3aed 60%, #a855f7)" />
        <CardGradient />
        <CardInfo>
          <h4>Sophia, 26</h4>
          <p>2 miles away</p>
        </CardInfo>
        <HeartBurst>❤️</HeartBurst>
      </CardMock>
      <SparkDot top="20%" left="15%" c="#e879f9" s="10px" dur="2.2s" del="0s" />
      <SparkDot
        top="70%"
        left="10%"
        c="#a855f7"
        s="6px"
        dur="1.8s"
        del="0.3s"
      />
      <SparkDot
        top="30%"
        right="15%"
        c="#c084fc"
        s="8px"
        dur="2.8s"
        del="0.6s"
      />
      <SparkDot top="75%" right="20%" c="#e879f9" s="12px" dur="2s" del="1s" />
    </StepVisual>
  );
}

function Step2Visual() {
  return (
    <StepVisual>
      <ConnectLine viewBox="0 0 400 400" fill="none">
        <circle
          cx="200"
          cy="200"
          r="90"
          stroke="rgba(168,85,247,0.15)"
          strokeWidth="1"
          strokeDasharray="4 6"
        />
        <circle
          cx="200"
          cy="200"
          r="140"
          stroke="rgba(232,121,249,0.08)"
          strokeWidth="1"
        />
        <line
          x1="200"
          y1="60"
          x2="200"
          y2="340"
          stroke="rgba(168,85,247,0.1)"
          strokeWidth="1"
          strokeDasharray="3 5"
        />
        <line
          x1="60"
          y1="200"
          x2="340"
          y2="200"
          stroke="rgba(168,85,247,0.1)"
          strokeWidth="1"
          strokeDasharray="3 5"
        />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
          const r = 140;
          const x = 200 + r * Math.cos((angle * Math.PI) / 180);
          const y = 200 + r * Math.sin((angle * Math.PI) / 180);
          return (
            <circle key={i} cx={x} cy={y} r="4" fill="rgba(168,85,247,0.5)" />
          );
        })}
      </ConnectLine>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 130,
          height: 130,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #4a0080, #7c3aed)",
          boxShadow:
            "0 0 60px rgba(168,85,247,0.5), 0 0 120px rgba(168,85,247,0.2)",
          zIndex: 1,
        }}
      >
        <span style={{ fontSize: "2.8rem" }}>⚡</span>
      </div>
      <SparkDot
        top="15%"
        left="50%"
        c="#e879f9"
        s="14px"
        dur="1.5s"
        del="0.2s"
      />
      <SparkDot top="50%" left="8%" c="#a855f7" s="10px" dur="2s" del="0.7s" />
      <SparkDot
        top="80%"
        left="45%"
        c="#c084fc"
        s="8px"
        dur="2.3s"
        del="0.1s"
      />
      <SparkDot
        top="55%"
        right="8%"
        c="#e879f9"
        s="12px"
        dur="1.8s"
        del="0.9s"
      />
    </StepVisual>
  );
}

function Step3Visual() {
  return (
    <StepVisual>
      <VisualRing s="320px" c="rgba(232,121,249,0.08)" dur="4s" del="0s" />
      <VisualRing s="240px" c="rgba(168,85,247,0.12)" dur="3s" del="0.4s" />
      <PhoneMock>
        <ChatBubble top="80px" mine={false}>
          <p>Hey, I saw you liked hiking 🏔️</p>
        </ChatBubble>
        <ChatBubble top="148px" mine={true}>
          <p>Yes! Favorite trail?</p>
        </ChatBubble>
        <ChatBubble top="210px" mine={false}>
          <p>Half Dome last summer ✨</p>
        </ChatBubble>
        <ChatBubble top="270px" mine={true}>
          <p>No way, me too! Coffee?</p>
        </ChatBubble>
      </PhoneMock>
      <SparkDot
        top="20%"
        right="18%"
        c="#e879f9"
        s="10px"
        dur="2.2s"
        del="0.3s"
      />
      <SparkDot
        top="65%"
        left="12%"
        c="#a855f7"
        s="8px"
        dur="1.9s"
        del="0.7s"
      />
      <SparkDot top="40%" left="18%" c="#c084fc" s="6px" dur="2.5s" del="0s" />
    </StepVisual>
  );
}

const VISUALS = [Step1Visual, Step2Visual, Step3Visual];

// ─── Component ─────────────────────────────────────────────────────────────
export default function HowAttractionWorks() {
  const wrapperRef = useRef(null);
  const trackRef = useRef(null);
  const introRef = useRef(null);
  const outroRef = useRef(null);
  const stepRefs = useRef([]);
  const stepContentRefs = useRef([]);
  const stepVisualRefs = useRef([]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const track = trackRef.current;

    // Total number of panels: 1 intro + 3 steps + 1 outro = 5
    const totalPanels = 5;
    const scrollDistance = (totalPanels - 1) * window.innerWidth;

    // Set wrapper height to control scroll distance
    wrapper.style.height = `${scrollDistance + window.innerHeight}px`;

    // Horizontal scroll on track
    const hScroll = gsap.to(track, {
      x: () => -scrollDistance,
      ease: "none",
      scrollTrigger: {
        trigger: wrapper,
        start: "top top",
        end: () => `+=${scrollDistance}`,
        scrub: 1,
        pin: ".sticky-container",
        anticipatePin: 1,
      },
    });

    const vw = window.innerWidth;

    // ── Intro: scrub in as soon as page loads / first scroll
    gsap.set(
      [
        introRef.current.querySelector(".eyebrow"),
        introRef.current.querySelector(".title"),
        introRef.current.querySelector(".sub"),
        introRef.current.querySelector(".scroll-hint"),
      ],
      { opacity: 0, y: 40 },
    );

    const introTl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapper,
        start: "top top",
        end: `+=${vw * 0.4}`,
        scrub: 0.6,
      },
    });
    introTl
      .to(
        introRef.current.querySelector(".eyebrow"),
        { opacity: 1, y: 0, ease: "power2.out" },
        0,
      )
      .to(
        introRef.current.querySelector(".title"),
        { opacity: 1, y: 0, ease: "power2.out" },
        0.08,
      )
      .to(
        introRef.current.querySelector(".sub"),
        { opacity: 1, y: 0, ease: "power2.out" },
        0.16,
      )
      .to(
        introRef.current.querySelector(".scroll-hint"),
        { opacity: 1, y: 0, ease: "power2.out" },
        0.26,
      );

    // Fade intro out as we leave panel 0
    gsap
      .timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: `+=${vw * 0.5}`,
          end: `+=${vw * 0.85}`,
          scrub: 0.6,
        },
      })
      .to(introRef.current, { opacity: 0, y: -50, ease: "power2.in" });

    // ── Each step panel: scrub in then scrub out
    stepContentRefs.current.forEach((content, i) => {
      if (!content) return;
      const visual = stepVisualRefs.current[i];
      const xIn = STEPS[i].dir === "left" ? -80 : 80;

      // Set starting state
      gsap.set(content, { opacity: 0, x: xIn });
      gsap.set(visual, { opacity: 0, scale: 0.88, y: 30 });

      const panelOffset = (i + 1) * vw; // where this panel starts in scroll space

      // Scrub IN: start while panel is still sliding in from the right
      gsap
        .timeline({
          scrollTrigger: {
            trigger: wrapper,
            start: () => `+=${panelOffset - vw * 0.65}`,
            end: () => `+=${panelOffset + vw * 0.1}`,
            scrub: 0.8,
          },
        })
        .to(content, { opacity: 1, x: 0, ease: "power2.out" }, 0)
        .to(visual, { opacity: 1, scale: 1, y: 0, ease: "power2.out" }, 0);

      // Scrub OUT: start fading before next panel arrives
      gsap
        .timeline({
          scrollTrigger: {
            trigger: wrapper,
            start: () => `+=${panelOffset + vw * 0.55}`,
            end: () => `+=${panelOffset + vw * 0.88}`,
            scrub: 0.8,
          },
        })
        .to(content, { opacity: 0, x: -xIn * 0.6, ease: "power2.in" }, 0)
        .to(visual, { opacity: 0, scale: 0.92, y: -20, ease: "power2.in" }, 0);
    });

    // ── Outro: scrub in early too
    gsap.set(outroRef.current, { opacity: 0, y: 60 });
    gsap
      .timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: () => `+=${4 * vw - vw * 0.65}`,
          end: () => `+=${4 * vw + vw * 0.1}`,
          scrub: 0.8,
        },
      })
      .to(outroRef.current, { opacity: 1, y: 0, ease: "power2.out" });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
      hScroll.kill();
    };
  }, []);

  return (
    <>
      <GlobalStyle />
      <Wrapper ref={wrapperRef}>
        <StickyContainer className="sticky-container">
          {/* Ambient orbs */}
          <Orb
            top="10%"
            left="-10%"
            size="400px"
            color="rgba(124,58,237,0.25)"
            dur="5s"
            delay="0s"
          />
          <Orb
            top="50%"
            right="-8%"
            size="350px"
            color="rgba(232,121,249,0.15)"
            dur="6s"
            delay="1s"
          />
          <Orb
            bottom="-10%"
            left="30%"
            size="300px"
            color="rgba(168,85,247,0.2)"
            dur="4.5s"
            delay="0.5s"
          />

          <Track ref={trackRef}>
            {/* ── Panel 0: Intro */}
            <Panel ref={introRef}>
              <IntroInner>
                <Eyebrow className="eyebrow">The science of desire</Eyebrow>
                <MainTitle className="title">
                  How <em>Attraction</em>
                  <br />
                  Really Works
                </MainTitle>
                <SubText className="sub">
                  Three moments. Zero overthinking.
                  <br />
                  This is what happens between the first glance and the first
                  date.
                </SubText>
              </IntroInner>
              <ScrollHint className="scroll-hint">
                <span>Scroll to explore</span>
                <ChevronWrap>
                  <Chevron d="0s" />
                  <Chevron d="0.2s" />
                </ChevronWrap>
              </ScrollHint>
            </Panel>

            {/* ── Panels 1–3: Steps */}
            {STEPS.map((step, i) => {
              const Visual = VISUALS[i];
              return (
                <Panel key={i} ref={(el) => (stepRefs.current[i] = el)}>
                  <StepGrid className={step.reverse ? "reverse" : ""}>
                    <div ref={(el) => (stepVisualRefs.current[i] = el)}>
                      <Visual />
                    </div>
                    <StepContent
                      dir={step.dir}
                      ref={(el) => (stepContentRefs.current[i] = el)}
                    >
                      <StepNumber>{step.number}</StepNumber>
                      <StepTag>
                        <div />
                        <span>{step.tag}</span>
                      </StepTag>
                      <StepTitle>{step.title}</StepTitle>
                      <StepDesc>{step.desc}</StepDesc>
                      <StatRow>
                        {step.stats.map((s, j) => (
                          <Stat key={j}>
                            <div>{s.val}</div>
                            <div>{s.label}</div>
                          </Stat>
                        ))}
                      </StatRow>
                    </StepContent>
                  </StepGrid>
                </Panel>
              );
            })}

            {/* ── Panel 4: Outro */}
            <Panel>
              <OutroInner ref={outroRef}>
                <Eyebrow className="eyebrow" style={{ opacity: 1 }}>
                  You already know
                </Eyebrow>
                <MainTitle style={{ opacity: 1 }}>
                  Ready to feel
                  <br />
                  the <em>spark?</em>
                </MainTitle>
                <SubText style={{ opacity: 1 }}>
                  Stop overthinking. Start connecting.
                  <br />
                  Your next great match is one swipe away.
                </SubText>
                <CtaButton>Find Your Match →</CtaButton>
              </OutroInner>
            </Panel>
          </Track>
        </StickyContainer>
      </Wrapper>
    </>
  );
}
